/**
 * Scenarios – shared presentational atoms:
 *   StatusBadge, ReconciliationStatus, ScenarioCard
 */

import { AlertCircle, Check, ChevronRight, Lock } from "lucide-react";
import { formatMoney, formatVariance } from "@/components/tekledger-ui";
import type { ScenarioSummary } from "@workspace/api-client-react";
import {
  formatTs,
  friendlyScenarioTitle,
  questionShortLabel,
} from "./utils";

// ─── Status badge ──────────────────────────────────────────────────────────────

export function StatusBadge({ status }: { status: string }) {
  if (status === "promoted") {
    return (
      <span className="sc-badge sc-badge--promoted">
        <Lock size={10} /> Promoted
      </span>
    );
  }
  return <span className="sc-badge sc-badge--draft">Draft</span>;
}

// ─── Reconciliation indicator ──────────────────────────────────────────────────

export function ReconciliationStatus({
  reconciled,
  difference,
}: {
  reconciled: boolean;
  difference: number;
}) {
  if (reconciled) {
    return (
      <span className="sc-recon sc-recon--ok">
        <Check size={11} /> Reconciled
      </span>
    );
  }
  return (
    <span className="sc-recon sc-recon--gap">
      <AlertCircle size={11} />
      Gap: {formatMoney(Math.abs(difference))}
    </span>
  );
}

// ─── Scenario summary card (list item) ────────────────────────────────────────

export function ScenarioCard({
  scenario,
  isSelected,
  onClick,
}: {
  scenario: ScenarioSummary;
  isSelected: boolean;
  onClick: () => void;
}) {
  const title = friendlyScenarioTitle(scenario);
  const noImpact = scenario.fyImpact === 0 && scenario.rolling12Impact === 0;

  return (
    <button
      type="button"
      className={`sc-card ${isSelected ? "sc-card--selected" : ""} ${
        scenario.status === "promoted" ? "sc-card--promoted" : ""
      }`}
      onClick={onClick}
      data-testid={`scenario-card-${scenario.scenarioId}`}
    >
      <div className="sc-card__head">
        <span className="sc-card__name" data-testid={`scenario-title-${scenario.scenarioId}`}>
          {title}
        </span>
        <StatusBadge status={scenario.status} />
      </div>
      <p className="sc-card__question">
        {questionShortLabel(scenario.questionKey)}
      </p>
      <p className="sc-card__dept">
        FY{scenario.fiscalYear} · Dept {scenario.departmentCode}
      </p>

      {noImpact ? (
        <div className="sc-card__pending" data-testid={`scenario-pending-${scenario.scenarioId}`}>
          Impact pending — add a driver and recompute
        </div>
      ) : (
        <div className="sc-card__impacts">
          <div>
            <span>FY Impact</span>
            <strong
              className={
                scenario.fyImpact > 0
                  ? "bad-text"
                  : scenario.fyImpact < 0
                  ? "good-text"
                  : ""
              }
            >
              {formatVariance(scenario.fyImpact)}
            </strong>
          </div>
          <div>
            <span>Rolling 12M</span>
            <strong
              className={
                scenario.rolling12Impact > 0
                  ? "bad-text"
                  : scenario.rolling12Impact < 0
                  ? "good-text"
                  : ""
              }
            >
              {formatVariance(scenario.rolling12Impact)}
            </strong>
          </div>
        </div>
      )}

      <div className="sc-card__footer">
        <span className="sc-card__updated">
          Updated {formatTs(scenario.updatedAt)}
        </span>
        <ChevronRight size={13} className="sc-card__arrow" />
      </div>
    </button>
  );
}
